import React, { useState, useRef } from 'react'
import ReactCrop, { centerCrop, convertToPixelCrop, makeAspectCrop } from 'react-image-crop'
import { FiCrop, FiImage, FiUploadCloud, FiX } from 'react-icons/fi'

import 'react-image-crop/dist/ReactCrop.css'
import { useDebounceEffect } from './useDebounceEffect'
import { canvasPreview } from './canvaPreview'
import { Toast } from '../../../utils/Toast'
import './style.css'

function centerAspectCrop(mediaWidth, mediaHeight, aspect) {
  return centerCrop(
    makeAspectCrop(
      {
        unit: '%',
        width: 90,
      },
      aspect,
      mediaWidth,
      mediaHeight,
    ),
    mediaWidth,
    mediaHeight,
  )
}

export function ModalImageCrop({
  uploadImageBanner,
  toggleModal,
  width = 1200,
  height = 675,
  aspect = 16 / 9,
  title = 'Cortar imagem',
}) {
  const [imgSrc, setImgSrc] = useState('')
  const [fileName, setFileName] = useState('')
  const [isSaving, setIsSaving] = useState(false)
  const previewCanvasRef = useRef(null)
  const imgRef = useRef(null)
  const [crop, setCrop] = useState()
  const [completedCrop, setCompletedCrop] = useState()
  const [scale] = useState(1)
  const [rotate] = useState(0)

  function onSelectFile(e) {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0]

      if (!file.type.startsWith('image/')) {
        Toast.warning('Selecione um arquivo de imagem válido')
        return
      }

      setCrop(undefined)
      setCompletedCrop(undefined)
      setFileName(file.name)
      const reader = new FileReader()
      reader.addEventListener('load', () => {
        setImgSrc(reader.result?.toString() || '')
      })
      reader.readAsDataURL(file)
    }
  }

  function onImageLoad(e) {
    const image = e.currentTarget
    const nextCrop = centerAspectCrop(image.width, image.height, aspect)
    setCrop(nextCrop)
    setCompletedCrop(convertToPixelCrop(nextCrop, image.width, image.height))
  }

  useDebounceEffect(
    async () => {
      if (
        completedCrop?.width &&
        completedCrop?.height &&
        imgRef.current &&
        previewCanvasRef.current
      ) {
        // We use canvasPreview as it's much faster than imgPreview.
        canvasPreview(
          imgRef.current,
          previewCanvasRef.current,
          completedCrop,
          scale,
          rotate,
        )
      }
    },
    100,
    [completedCrop, scale, rotate],
  )

  function reset() {
    setImgSrc('')
    setFileName('')
    setCrop(undefined)
    setCompletedCrop(undefined)
    setIsSaving(false)
  }

  function closeModal() {
    reset()
    toggleModal?.()
  }

  async function saveCrop() {
    if (!completedCrop?.width || !completedCrop?.height || !imgRef.current || !previewCanvasRef.current) {
      return Toast.warning('Selecione e ajuste uma imagem antes de salvar')
    }

    setIsSaving(true)

    try {
      await canvasPreview(imgRef.current, previewCanvasRef.current, completedCrop, scale, rotate)
      const outputCanvas = document.createElement('canvas')
      outputCanvas.width = width
      outputCanvas.height = height
      const outputContext = outputCanvas.getContext('2d')
      outputContext.imageSmoothingEnabled = true
      outputContext.imageSmoothingQuality = 'high'
      outputContext.drawImage(previewCanvasRef.current, 0, 0, width, height)

      const blob = await new Promise((resolve, reject) => {
        outputCanvas.toBlob(
          result => result ? resolve(result) : reject(new Error('Não foi possível gerar o recorte')),
          'image/png',
        )
      })

      await uploadImageBanner(blob)
      document.getElementById('btn-close-crop').click()
    } catch (error) {
      Toast.warning(error.message || 'Não foi possível salvar a imagem')
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <div className="modal fade image-crop-modal" id="modalCropImage" data-bs-backdrop="static" data-bs-keyboard="false" tabIndex="-1" aria-labelledby="modalCropImageLabel" aria-hidden="true">
      <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
        <div className="modal-content">
          <div className="modal-header image-crop-header">
            <div className="image-crop-heading">
              <span><FiCrop /></span>
              <div>
                <h5 className="modal-title" id="modalCropImageLabel">{title}</h5>
                <p>Selecione a área que será exibida após o envio.</p>
              </div>
            </div>
            <button type="button" id="btn-close-crop" className="image-crop-close" data-bs-dismiss="modal" aria-label="Fechar" onClick={closeModal}><FiX /></button>
          </div>
          <div className="modal-body">
            <label className="image-crop-picker">
              <FiUploadCloud />
              <span>{fileName || 'Escolher imagem'}</span>
              <small>PNG, JPG ou WEBP. Proporção recomendada: {Math.abs(aspect - 1) < 0.01 ? '1:1' : '16:9'}.</small>
              <input type="file" accept="image/*" onChange={onSelectFile} />
            </label>

            {!imgSrc ? (
              <div className="image-crop-empty">
                <FiImage />
                <strong>Nenhuma imagem selecionada</strong>
                <span>Escolha uma imagem para liberar a ferramenta de recorte.</span>
              </div>
            ) : (
              <div className="image-crop-workspace">
                <section className="image-crop-editor">
                  <div className="image-crop-section-title"><strong>Ajustar recorte</strong><span>Arraste a área ou redimensione pelas bordas.</span></div>
                  <div className="image-crop-stage">
                      <ReactCrop
                        crop={crop}
                        onChange={(_, percentCrop) => setCrop(percentCrop)}
                        onComplete={(c) => setCompletedCrop(c)}
                        aspect={aspect}
                        minWidth={80}
                      >
                        <img
                          ref={imgRef}
                          alt="Imagem para recortar"
                          src={imgSrc}
                          onLoad={onImageLoad}
                          style={{ maxWidth: '100%', maxHeight: '52vh' }}
                        />
                      </ReactCrop>
                  </div>
                </section>

                <aside className="image-crop-preview">
                  <div className="image-crop-section-title"><strong>Prévia</strong><span>Resultado que será enviado.</span></div>
                  <div className="image-crop-preview-frame" style={{ aspectRatio: aspect }}>
                    <canvas ref={previewCanvasRef} />
                  </div>
                  <small>Saída proporcional a {width} x {height}px.</small>
                </aside>
              </div>
            )}
          </div>
          <div className="modal-footer image-crop-footer">
            <button type="button" className="image-crop-secondary" data-bs-dismiss="modal" onClick={closeModal}>Cancelar</button>
            <button type="button" className="image-crop-primary" onClick={saveCrop} disabled={!completedCrop?.width || isSaving}>{isSaving ? 'Salvando...' : 'Salvar recorte'}</button>
          </div>
        </div>
      </div>
    </div>
  )
}
